import React, { useState } from 'react';
import SearchInput from 'components/search/SearchInput';
import useBlogNavbar from 'ui/src/hooks/useBlogNavbar';
import twMerge from 'utilities/src/twMerge';
import AccordionTitle from './_AccordionTitle';
import SearchButton from './_SearchButton';
import LinkItem from './_LinkItem';
import useSearch from './_useSearch';
import BackgroundWrapper from './_BackgroundWrapper';
import GradientCorner from './_GradientCorner';
import getRelativePath from './_getRelativePath';

interface Props {
  list: {
    title: string;
    links: { label: string; href: string }[];
  };
  visibleInPaths: string[];
}

export default function Navigation({ list, visibleInPaths }: Props) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const { isDark, isInvisible, activeItem, pathname } = useBlogNavbar();

  const { showSearch, openSearch, navRef, onKeyDown, onBlur } = useSearch();

  //   @TODO: Control through layouts when moving to app folder
  if (!visibleInPaths.includes(pathname)) {
    return null;
  }

  return (
    <nav
      onBlur={onBlur}
      ref={navRef}
      className={twMerge(
        'site-paragraph-s mdL:site-paragraph-m fixed top-24 z-30 w-full',
        'transition-all duration-300',
        'text-site-neutrals-600 mdS:text-site-neutrals-400',
        {
          'text-site-neutrals-0 mdS:text-site-neutrals-50': isDark,
          'pointer-events-none opacity-0': isInvisible,
        },
      )}
    >
      <div className="mdL:mx-8 mdL:flex-row relative flex flex-col lg:mx-auto lg:w-full lg:max-w-[76rem]">
        <div
          className={twMerge('mdS:flex mdS:flex-row-reverse mdS:h-14 mdL:h-auto mdL:min-w-0', {
            'mdS:max-mdL:hidden hidden': showSearch,
            'mdL:flex-1 mdL:rounded-r-lg mdL:overflow-hidden': !showSearch,
          })}
        >
          <BackgroundWrapper
            isDark={isDark}
            classes={twMerge('mdL:pr-3 mdL:py-2.5 flex items-center justify-between px-4 py-2', {
              'mdL:hidden': showSearch,
            })}
          >
            <AccordionTitle onClick={() => setIsAccordionOpen(!isAccordionOpen)} title={list.title} />
            <SearchButton onClick={openSearch} classes={twMerge({ 'text-site-neutrals-0': isDark })} />
          </BackgroundWrapper>
          <div
            className={twMerge(
              'mdS:flex-1 grid overflow-hidden transition-[grid-template-rows]',
              { 'max-mdS:grid-rows-[0fr]': !isAccordionOpen },
              { 'grid-rows-[1fr]': isAccordionOpen },
              { 'mdL:flex hidden': showSearch },
            )}
          >
            <BackgroundWrapper
              isDark={isDark}
              classes={twMerge('mdS:rounded-none mdL:rounded-l-lg overflow-hidden rounded-b-2xl mdL:pr-9', {
                'max-mdS:bg-site-neutrals-800': isDark,
              })}
            >
              <ul
                className={twMerge(
                  'mdS:flex mdS:h-full mdS:border-none mdS:py-0 border-t py-2',
                  'border-site-neutrals-100',
                  { 'border-site-neutrals-600': isDark },
                )}
              >
                {list.links.map((link, index) => (
                  <li key={link.label}>
                    <LinkItem
                      href={link.href}
                      label={link.label}
                      isActive={getRelativePath(link.href) === activeItem}
                      isDark={isDark}
                      index={index}
                    />
                  </li>
                ))}
              </ul>
            </BackgroundWrapper>
          </div>
        </div>
        <BackgroundWrapper
          isDark={isDark}
          classes={twMerge(
            'mdL:rounded-r-lg z-10 mdL:flex-grow flex mdL:flex-shrink-0 mdL:min-w-[32.5rem] mdL:pl-2 px-4 mdL:pr-3 relative',
            {
              hidden: !showSearch,
            },
          )}
        >
          <GradientCorner isDark classes={twMerge({ 'opacity-0': !isDark })} />
          <GradientCorner isDark={false} classes={twMerge({ 'opacity-0': isDark })} />
          <SearchInput
            id="blog-search"
            classes="py-3 mdS:py-2.5 gap-2 !pl-0 [&_input]:placeholder:text-site-neutrals-400 flex-1"
            handleKeyDown={onKeyDown}
            buttonClasses="h-9 px-1.5"
            inputSize="Small"
          />
        </BackgroundWrapper>
      </div>
    </nav>
  );
}
